import { IconType } from 'react-icons'
import { MdHelpOutline } from 'react-icons/md'
import DeltaBadge from './delta-badge'
import { deltaOf, Polarity } from './marketing-format'

interface MarketingKpiCardProps {
    title: string
    value: number | null
    previous?: number | null
    format: (value: number) => string
    icon: IconType
    color: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary' | 'accent'
    polarity?: Polarity
    subtitle?: string
    onHelp?: () => void
    /** Valor ainda indisponível (ex.: investimento antes da sincronização). */
    unavailable?: string
}

/**
 * Indicador com o valor do período, o do período comparado e a variação. Mesmo
 * desenho do card do dashboard financeiro, mais a linha de comparação.
 */
export default function MarketingKpiCard({
    title,
    value,
    previous,
    format,
    icon: Icon,
    color,
    polarity = 'higher-better',
    subtitle,
    onHelp,
    unavailable,
}: MarketingKpiCardProps) {
    const delta = previous === undefined ? null : deltaOf(value, previous, polarity)
    const display = unavailable ? '—' : value == null ? '—' : format(value)
    const previousDisplay = previous == null ? '—' : format(previous)

    return (
        <div
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br from-base-100 to-base-200/50 border border-base-300/50 hover:border-${color}/30 transition-all duration-300 hover:shadow-md`}
        >
            <div className="absolute top-0 right-0 w-20 h-20 opacity-5">
                <Icon className="text-[5rem] -rotate-12" />
            </div>

            <div className="relative p-4 flex flex-col gap-2 h-full">
                <div className="flex items-start justify-between gap-1">
                    <div className={`inline-flex p-2 rounded-lg bg-${color}/10 w-fit`}>
                        <Icon className={`text-base text-${color}`} />
                    </div>
                    <div className="flex items-center gap-1">
                        {!unavailable && <DeltaBadge delta={delta} title={`Antes: ${previousDisplay}`} />}
                        {onHelp && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                                onClick={onHelp}
                                aria-label={`O que é ${title}?`}
                                title={`O que é ${title}?`}
                            >
                                <MdHelpOutline className="text-base" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="min-w-0">
                    <p className="text-[10px] font-medium text-base-content/50 uppercase tracking-wide truncate">
                        {title}
                    </p>
                    <p className="text-lg font-bold text-base-content mt-0.5 truncate" title={display}>
                        {display}
                    </p>
                    {unavailable ? (
                        <p className="text-[11px] text-base-content/50 leading-snug">{unavailable}</p>
                    ) : (
                        <>
                            {previous !== undefined && (
                                <p className="text-[11px] text-base-content/50 truncate">
                                    Antes: {previousDisplay}
                                </p>
                            )}
                            {subtitle && (
                                <p className="text-[11px] text-base-content/60 leading-snug line-clamp-2" title={subtitle}>
                                    {subtitle}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
